import React, { useState, useEffect } from 'react';
import { Paper, Tabs, Tab } from "@mui/material";
import Chip from "@mui/material/Chip";

import { List, IListItemProps } from 'components/ui/Lists';
import { LabelledCheckbox } from 'components/ui/LabelledCheckbox';
import { IConfirmProps } from 'components/ui/ConfirmationBox';
import { TabPanel } from 'components/ui/TabPanel';
import PortalPage from 'components/portal/PortalPage';
import { ToolTipButton } from 'components/menu/Labels';
import { FatigueAssessmentQuestions } from 'components/portal/fatigueAssessment/FatigueAssessmentQuestions'

import { FatigueAssessmentReportQuery, FatigueAssessmentStatusEnum, FatigueAssessmentRiskEnum } from 'shared/models/api/models';
import * as api from 'shared/services/reportingApi';
import * as locationApi from 'shared/services/locationApi';

import { ReactComponent as ReportingIcon } from 'assets/icons/reports-filled.svg';
import { ReactComponent as BuildCircle } from 'assets/icons/build-circle.svg';
import { ReactComponent as AddChart } from 'assets/icons/add-chart.svg';

import 'react-datepicker/dist/react-datepicker.css';
import 'components/ui/ConfirmationBox.css';
import 'components/portal/administration/administration.css';
import './Reporting.css';

import {
    SupervisorAccountOutlined, PersonOutlined, NearMeOutlined, MailOutline,
    LocationOnOutlined, AssignmentTurnedInOutlined, CancelOutlined, DateRangeOutlined, CheckOutlined
} from '@mui/icons-material';
import { blobDownload } from './blobDownload';
import DateRangePicker from './DateRangePicker';


export const UserListItem = (props: IListItemProps<api.User>) =>
    <div className="list-item user-list-item" onClick={() => props.toggle(props.item)}>
        <LabelledCheckbox checkedValue={props.selected === true}><span className="name">{props.item.fullName}</span></LabelledCheckbox>
        <div>
            {props.item.city &&
                <span className="location"><NearMeOutlined /> {props.item.city}</span>
            }

            {props.item.supervisor.length > 0 &&
                <span className="supervisor"><SupervisorAccountOutlined />{props.item.supervisor}</span>
            }

            {!props.item.city && props.item.email.length > 0 &&
                <span className="email"><MailOutline />{props.item.email}</span>
            }

        </div>
    </div>;

export const LocationListItem = (props: IListItemProps<api.Location>) =>
    <div className="list-item user-list-item" onClick={() => props.toggle(props.item)}>
        <LabelledCheckbox checkedValue={props.selected === true}><span className="name">{props.item.name}</span></LabelledCheckbox>
    </div>;


interface IChipsProps<T> {
    values: T[],
    removeItem: (item: T) => void,
    prefix?: any
}

const UserChips = (props: IChipsProps<api.User>) =>
    <>
        {props.values &&
            props.values.map(usr =>
                <Chip
                    key={usr.id}
                    onDelete={() => { props.removeItem(usr); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{usr.fullName}
                    </>} />
            )
        }
    </>;

const LocationChips = (props: IChipsProps<api.Location>) =>
    <>
        {props.values &&
            props.values.map(usr =>
                <Chip
                    key={`locChip-${ usr.id }`}
                    onDelete={() => { props.removeItem(usr); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{usr.name}
                    </>} />
            )
        }
    </>;

const FatigueAssessmentReport = () => {
    const [exportState, setExportState] = useState("ready" as "ready" | "busy");
    const [stateVersion, setStateVersion] = useState(0);
    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);
    const [userFilterTab, setUserFilterTab] = useState(0);

    const [selectedUsers, setSelectedUsers] = useState([] as api.User[]);
    const [selectAllUsers, setSelectAllUsers] = useState(true);
    const [usersByNameFilter, setUsersByNameFilter] = useState("");
    const [usersByNameExpanded, setUsersByNameExpanded] = useState(false);
    const [usersByNameSummary, setUsersByNameSummary] = useState("");
    const [usersByName, setUsersByName] = useState([] as api.User[]);

    const [selectedSupervisors, setSelectedSupervisors] = useState([] as api.User[]);
    const [supervisorsFilter, setSupervisorsFilter] = useState("");
    const [supervisorsExpanded, setSupervisorsExpanded] = useState(false);
    const [supervisorsSummary, setSupervisorsSummary] = useState("");
    const [supervisors, setSupervisors] = useState([] as api.User[]);

    const [selectedLocations, setSelectedLocations] = useState([{ name: "All" }] as api.Location[]);
    const [locationsFilter, setLocationsFilter] = useState("");
    const [locationsExpanded, setLocationsExpanded] = useState(false);
    const [locationsSummary, setLocationsSummary] = useState("");
    const [locations, setLocations] = useState([] as api.Location[]);
    const [includeAudit, setincludeAudit] = useState(false);

    const [statusCompleted, setStatusCompleted] = useState(true);
    const [statusPending, setStatusPending] = useState(true);
    const [statusViolation, setStatusViolation] = useState(true);

    const [riskLow, setRiskLow] = useState(true);
    const [riskMedium, setRiskMedium] = useState(true);
    const [riskHigh, setRiskHigh] = useState(true);
    const [riskNa, setRiskNa] = useState(true);

    const today = new Date()
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const [shiftDateFrom, setShiftDateFrom] = useState(yesterday as Date | null);
    const [shiftDateTo, setShiftDateTo] = useState(today as Date | null);

    const setShiftDateRange = (dateFrom: Date | null, dateTo: Date | null): void => {
        setShiftDateFrom(dateFrom);
        setShiftDateTo(dateTo);
    };

    const forceStateChange = () => {
        setStateVersion(stateVersion + 1);
    };

    const selectItems = <T,>(
        srcItems: T[],
        destinationItems: T[],
        comparer: (a: T, b: T) => boolean,
        setter: (items: T[]) => void
    ) => {
        for (var i = 0; i < srcItems.length; i++) {
            // if its not already selected, add it to the list
            if (destinationItems.findIndex(d => comparer(d, srcItems[i])) === -1) {
                destinationItems.push(srcItems[i]);
            }
        }

        setter(destinationItems);
        forceStateChange();
    };

    const getExportFilter = () => {
        let userIds = selectedUsers.map(user => user.id);
        let supervisorIds = selectedSupervisors.map(user => user.id);

        let locationIds = [] as number[];
        selectedLocations.forEach(location => {
            if (typeof location.id === 'number') {
                locationIds.push(location.id);
            }
        });

        let statusIds = [] as FatigueAssessmentStatusEnum[];
        if (statusCompleted) statusIds.push(FatigueAssessmentStatusEnum.Complete);
        if (statusPending) statusIds.push(FatigueAssessmentStatusEnum.Pending);
        if (statusViolation) statusIds.push(FatigueAssessmentStatusEnum.Violation);

        let riskIds = [] as FatigueAssessmentRiskEnum[];
        if (riskLow) riskIds.push(FatigueAssessmentRiskEnum.Low);
        if (riskMedium) riskIds.push(FatigueAssessmentRiskEnum.Medium);
        if (riskHigh) riskIds.push(FatigueAssessmentRiskEnum.High);
        if (riskNa) riskIds.push(FatigueAssessmentRiskEnum.NA);

        return {
            userIds: userIds,
            supervisorIds: supervisorIds,
            locationIds: locationIds,
            statusIds: statusIds,
            riskIds: riskIds,
            shiftDateFrom: shiftDateFrom,
            shiftDateTo: shiftDateTo,
            includeAudit: includeAudit
        } as FatigueAssessmentReportQuery;
    };

    const downloadExportResults = async () => {
        if (exportState === "ready") {
            const filter = getExportFilter();

            if (filter) {
                setExportState("busy");

                api.downloadFatigueAssessmentResults(filter)
                    .then(file => {
                        setExportState("ready");
                        blobDownload(file);
                    })
                    .catch(e => {
                        const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                        confirm(message, ["Close"], "Close");
                        setExportState("ready");
                    });
            }
        }
    };

    const toggleItem = <T,>(
        item: T,
        arr: T[],
        comparerPredicate: (value: T) => boolean,
        setter: (value: T[]) => void
    ): boolean => {
        const idx = arr.findIndex(comparerPredicate);

        var result = false;

        if (idx === -1) {
            result = true;
            arr.push(item);
        } else {
            arr.splice(idx, 1);
        }

        setter(arr);
        forceStateChange();

        return result;
    };

    // when the user changes a user filter, turn on exporting users, and turn off "Select All Users"
    const userFilterAdded = () => {
        if (selectAllUsers) {
            setSelectAllUsers(false);
        }
    }

    const toggleUser = (usr: api.User) => {
        if (toggleItem(usr, selectedUsers, u => { return u.id === usr.id }, setSelectedUsers) === true) {
            userFilterAdded();
        }
    };

    const toggleSupervisor = (usr: api.User) => {
        if (toggleItem(usr, selectedSupervisors, u => { return u.id === usr.id }, setSelectedSupervisors) === true) {
            userFilterAdded();
        }
    };

    const toggleLocation = (usr: api.Location) => {
        let locations = selectedLocations;

        if (locations.length > 0 && locations[0].id == undefined) {
            locations = [];
            if (usr.id == undefined) {
                setSelectedLocations(locations);
                return;
            }
        }

        toggleItem(usr, locations, u => { return u.id === usr.id }, setSelectedLocations);
    };

    const searchUsersByName = async (name: string) => {
        const result = await api.getUsersByName(name);

        if (result) {
            setUsersByName(result);
            setUsersByNameSummary(result.length + " search results for the search \"" + name + "\"");
        }

        return result;
    };

    const searchSupervisorsByName = async (name: string) => {
        const result = await api.getSupervisorsByName(name);

        if (result) {
            setSupervisors(result);
            setSupervisorsSummary(result.length + " search results for the search \"" + name + "\"");
        }

        return result;
    };

    const getLocations = async () => {
        const result = await locationApi.getLocations();

        if (result) {
            setLocations(result);
            setLocationsSummary(result.length + " locations results");
        }

        return result;
    };

    const userNameItemFilter = (item: api.User, filterText: string) => {
        const lcName = filterText.toLowerCase();

        return (
            (item.fullName).toLowerCase().indexOf(lcName) > -1
            || (item.email?.toLowerCase().indexOf(lcName) > -1)
        );
    };

    const confirm = async (message: string, opts?: string[], defaultOption?: string, cancelOption?: string): Promise<string> => {
        return new Promise<string>((resolve) => {
            setConfirmProps({
                message: message,
                options: opts ?? ["OK", "Cancel"],
                defaultOption: defaultOption,
                setResult: (value) => {
                    setConfirmProps({} as IConfirmProps);
                    resolve(value);
                }
            });
        });
    };

    const setSelectAllUsersAndClearSelections = (value: boolean) => {
        if (value) {
            confirm("Are you sure you want to include all users? Including all users will reset all user filters.", ["All Users", "Cancel"], "All Users").then(result => {
                if (result === "All Users") {
                    setSelectedUsers([]);
                    setSelectedSupervisors([]);

                    setSelectAllUsers(true);
                } else {
                    setSelectAllUsers(false);
                }
            });
        } else {
            setSelectAllUsers(value);
        }
    };

    useEffect(() => {
        getLocations();
    }, []);

    const View = () => {
        return (
            <div className="main-body-container reporting-module">

                <div className="section-1">
                    <div className="header">
                        <span className="section-1 icon"><BuildCircle /></span>
                        <span className="section-1 text">Build a report</span>
                    </div>
                </div>

                <div className="section-2">
                    <div className="header">
                        <span className="icon"><AddChart /></span>
                        <span className="text">Add data</span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><PersonOutlined /></span>
                        <span className="text">User</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox
                            checkedValue={selectAllUsers}
                            toggle={() => { setSelectAllUsersAndClearSelections(!selectAllUsers); }}>Select all users</LabelledCheckbox>
                    </div>
                    <div className="body">or</div>
                    <div className="body">
                        <Paper>
                            <Tabs value={userFilterTab} onChange={(e, v) => { setUserFilterTab(v); }}>
                                <Tab label="Find a user" />
                                <Tab label="Users by supervisor" />
                            </Tabs>

                            <TabPanel value={userFilterTab} index={0}>
                                <List<api.User>
                                    summary={usersByNameSummary}
                                    dataSource={usersByName}
                                    listItemType={UserListItem}
                                    filterText={usersByNameFilter}
                                    setFilterText={setUsersByNameFilter}
                                    expanded={usersByNameExpanded}
                                    setExpanded={setUsersByNameExpanded}
                                    buildKey={item => item.id}
                                    filterAsync={searchUsersByName}
                                    toggleSelected={toggleUser}
                                    selectItems={
                                        (filteredItems) => selectItems(
                                            filteredItems,
                                            selectedUsers,
                                            (a, b) => a.id === b.id,
                                            setSelectedUsers
                                        )
                                    }
                                    noResultsMessage="No users found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedUsers} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={1}>
                                <List<api.User>
                                    summary={supervisorsSummary}
                                    dataSource={supervisors}
                                    listItemType={UserListItem}
                                    filterText={supervisorsFilter}
                                    setFilterText={setSupervisorsFilter}
                                    expanded={supervisorsExpanded}
                                    setExpanded={setSupervisorsExpanded}
                                    buildKey={item => item.id}
                                    filterAsync={searchSupervisorsByName}
                                    toggleSelected={toggleSupervisor}
                                    selectItems={
                                        (filteredItems) => selectItems(
                                            filteredItems,
                                            selectedSupervisors,
                                            (a, b) => a.id === b.id,
                                            setSelectedSupervisors
                                        )
                                    }
                                    noResultsMessage="No supervisors found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedSupervisors} />
                            </TabPanel>

                        </Paper>

                        <span>
                            <UserChips prefix="User" values={selectedUsers} removeItem={toggleUser} />
                            <UserChips prefix="Supervisor" values={selectedSupervisors} removeItem={toggleSupervisor} />
                            &nbsp;
                            {selectAllUsers &&
                                <Chip onDelete={() => { setSelectAllUsersAndClearSelections(false); }} label={<><b>User: </b>All</>}></Chip>
                            }
                            &nbsp;
                        </span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><LocationOnOutlined /></span>
                        <span className="text">{FatigueAssessmentQuestions.signOnLocation.value}</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <div className="tab-panel-location" onClick={() => setLocationsExpanded(true)}>
                            <List<api.Location>
                                summary={locationsSummary}
                                dataSource={locations}
                                listItemType={LocationListItem}
                                filterText={locationsFilter}
                                setFilterText={setLocationsFilter}
                                expanded={locationsExpanded}
                                setExpanded={setLocationsExpanded}
                                buildKey={item => item.id}
                                toggleSelected={toggleLocation}
                                selectItems={
                                    (filteredItems) => selectItems(
                                        filteredItems,
                                        selectedLocations,
                                        (a, b) => a.id === b.id,
                                        setSelectedLocations
                                    )
                                }
                                noResultsMessage="No locations found"
                                filterTextPlaceholder="Select location"
                                selected={selectedLocations} />
                        </div>

                        <span>
                            <LocationChips prefix="Location" values={selectedLocations} removeItem={toggleLocation} />
                        </span>

                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><AssignmentTurnedInOutlined /></span>
                        <span className="text">Status</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox checkedValue={statusCompleted} toggle={() => setStatusCompleted(!statusCompleted)}>Completed fatigue assessments</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusPending} toggle={() => setStatusPending(!statusPending)}>Pending fatigue assessments</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={statusViolation} toggle={() => setStatusViolation(!statusViolation)}>Fatigue assessments not complete</LabelledCheckbox>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><CancelOutlined /></span>
                        <span className="text">Risk</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox checkedValue={riskLow} toggle={() => setRiskLow(!riskLow)}>Low risk</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={riskMedium} toggle={() => setRiskMedium(!riskMedium)}>Medium risk</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={riskHigh} toggle={() => setRiskHigh(!riskHigh)}>High risk</LabelledCheckbox>
                        <LabelledCheckbox checkedValue={riskNa} toggle={() => setRiskNa(!riskNa)}>
                            Not applicable
                            <ToolTipButton text={<>Only applies to: <br />pending / not completed fatigue assessments </>} />
                        </LabelledCheckbox>

                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><DateRangeOutlined /></span>
                        <span className="text">{FatigueAssessmentQuestions.partialShiftStartDate.value}</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <DateRangePicker
                            fromDate={shiftDateFrom ?? yesterday}
                            toDate={shiftDateTo ?? today}
                            setter={(f, t) => {
                                setShiftDateRange(f ? f : null, t ? t : null);
                            }}
                        />
                    </div>
                </div>
                <div className="section-3">
                    <div className="header">
                        <span className="icon"><CheckOutlined /></span>
                        <span className="text">Audit</span>
                    </div>
                    <div className="divider" />
                </div>
                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox checkedValue={includeAudit} toggle={() => setincludeAudit(!includeAudit)}>Include fatigue assessment audit trail</LabelledCheckbox>

                    </div>
                </div>
                <div className="section-3">
                    <div className="body" style={{ textAlign: "center" }}>
                        <button className={"button-face export-state-" + exportState} onClick={downloadExportResults}>Export to CSV</button>
                    </div>
                </div>

                {((exportState === 'busy')) &&
                    <div className="export-busy-container">
                        <div className="export-busy-message">We're fetching your data...</div>
                    </div>
                }

            </div >
        );
    };

    return (
        <PortalPage module=""
            page="reporting"
            title={"Fatigue Assessment reporting"}
            route={"/reporting/fatigue-assessment"}
            loadingState={exportState}
            icon={ReportingIcon}
            confirmationProps={confirmProps}>

            <View />
        </PortalPage>
    );
};

export default FatigueAssessmentReport;
